/**
 * CR315: Search Scope Configuration Section
 * 
 * Controls for search behavior and which fields to search.
 * CRITICAL: Search scope must never include terms/fine print blobs.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Search, Shield } from "lucide-react";
import type { SearchFilterConfig } from "@/lib/layout-studio/types";
import { DEFAULT_SEARCH_FILTER_CONFIG } from "@/lib/layout-studio/types";

interface SearchScopeSectionProps {
  config: SearchFilterConfig;
  onChange: (config: SearchFilterConfig) => void;
}

type ScopeField = "title" | "condition" | "reward" | "merchant";

const SCOPE_FIELD_LABELS: Record<ScopeField, string> = {
  title: "Title",
  condition: "Condition",
  reward: "Reward",
  merchant: "Merchant",
};

export function SearchScopeSection({ config, onChange }: SearchScopeSectionProps) {
  const mergedConfig = { ...DEFAULT_SEARCH_FILTER_CONFIG, ...config };

  const handlePlaceholderChange = (value: string) => {
    onChange({ ...mergedConfig, placeholderText: value });
  };

  const handleDebounceChange = (value: number) => {
    onChange({ ...mergedConfig, debounceMs: value });
  };

  const handleMinCharsChange = (value: number) => {
    onChange({ ...mergedConfig, minChars: value });
  };

  const handleScopeChange = (scope: SearchFilterConfig["scope"]) => {
    onChange({ ...mergedConfig, scope });
  };

  const handleScopeFieldToggle = (field: ScopeField, enabled: boolean) => {
    const newFields = enabled
      ? [...mergedConfig.scopeFields.filter(f => f !== field), field]
      : mergedConfig.scopeFields.filter(f => f !== field);
    
    // Ensure at least one field is selected
    if (newFields.length === 0) return;
    
    onChange({ ...mergedConfig, scopeFields: newFields as ScopeField[] });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Search className="h-4 w-4" />
          Search
        </CardTitle>
        <CardDescription>
          Configure search behavior and scope.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Placeholder Text */}
        <div className="space-y-2">
          <Label className="text-xs">Placeholder Text</Label>
          <Input
            value={mergedConfig.placeholderText}
            onChange={(e) => handlePlaceholderChange(e.target.value)}
            placeholder="Search deals"
            className="h-8 text-xs"
          />
        </div>

        {/* Debounce */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Debounce Delay</Label>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {mergedConfig.debounceMs}ms
            </span>
          </div>
          <Slider
            value={[mergedConfig.debounceMs]}
            onValueChange={([v]) => handleDebounceChange(v)}
            min={0}
            max={500}
            step={50}
            className="w-full"
          />
        </div>

        {/* Min Characters */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Minimum Characters</Label>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {mergedConfig.minChars}
            </span>
          </div>
          <Slider
            value={[mergedConfig.minChars]}
            onValueChange={([v]) => handleMinCharsChange(v)}
            min={0}
            max={5}
            step={1}
            className="w-full"
          />
        </div>

        {/* Search Scope */}
        <div className="space-y-2">
          <Label className="text-xs">Search Scope</Label>
          <Select
            value={mergedConfig.scope}
            onValueChange={(v) => handleScopeChange(v as SearchFilterConfig["scope"])}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="card_visible">
                <div className="flex items-center gap-2">
                  Card Visible Fields
                  <Badge variant="secondary" className="text-[8px] px-1">Recommended</Badge>
                </div>
              </SelectItem>
              <SelectItem value="title_only">Title Only</SelectItem>
              <SelectItem value="title_merchant">Title + Merchant</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Scope Fields (when card_visible is selected) */}
        {mergedConfig.scope === "card_visible" && (
          <div className="space-y-2">
            <Label className="text-xs">Fields to Search</Label>
            <div className="flex flex-wrap gap-3">
              {(Object.keys(SCOPE_FIELD_LABELS) as ScopeField[]).map((field) => (
                <div key={field} className="flex items-center space-x-2">
                  <Checkbox
                    id={`scope-${field}`}
                    checked={mergedConfig.scopeFields.includes(field)}
                    onCheckedChange={(v) => handleScopeFieldToggle(field, !!v)}
                    disabled={
                      mergedConfig.scopeFields.length === 1 && 
                      mergedConfig.scopeFields[0] === field
                    }
                  />
                  <Label htmlFor={`scope-${field}`} className="text-xs cursor-pointer">
                    {SCOPE_FIELD_LABELS[field]}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security Note */}
        <Alert className="py-2">
          <Shield className="h-3 w-3" />
          <AlertDescription className="text-[10px]">
            Search is restricted to card-visible fields only. Terms and fine print are never searched.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
